import type { useNavigate } from "@tanstack/react-router";
import { v4 as uuidv4 } from "uuid";
import { transcribeAudioApiTranscribePost } from "../generated/api/sdk.gen";
import { getErrorMessage } from "./api-client";
import {
  generationReferenceState,
  type ReferenceAttachment,
} from "./reference-attachment";

export interface RunGenerateParams {
  prompt: string;
  projectId: string | undefined;
  references: ReferenceAttachment[];
  navigate: ReturnType<typeof useNavigate>;
  setIsGenerating: (v: boolean) => void;
  setError: (err: string | null) => void;
}

export async function runGenerate({
  prompt,
  projectId,
  references,
  navigate,
  setIsGenerating,
  setError,
}: RunGenerateParams): Promise<void> {
  if (!prompt.trim()) {
    alert("Please enter a prompt");
    return;
  }
  setIsGenerating(true);
  setError(null);
  try {
    navigate({
      to: "/results",
      search: {
        sessionId: uuidv4(),
        prompt,
        requestId: uuidv4(),
        projectId,
      },
      state: generationReferenceState(references),
    });
  } catch (err) {
    setError(getErrorMessage(err));
    setIsGenerating(false);
  }
}

export interface TranscribeAudioFileParams {
  audioFile: File;
  setPrompt: React.Dispatch<React.SetStateAction<string>>;
  setError: (err: string | null) => void;
  setIsTranscribing: (v: boolean) => void;
  audioInputRef: React.RefObject<HTMLInputElement | null>;
}

export async function transcribeAudioFile({
  audioFile,
  setPrompt,
  setError,
  setIsTranscribing,
  audioInputRef,
}: TranscribeAudioFileParams): Promise<void> {
  setIsTranscribing(true);
  setError(null);
  try {
    const { data } = await transcribeAudioApiTranscribePost({
      body: { audio: audioFile },
    });
    setPrompt((prev) => prev + (prev.trim() ? " " : "") + data.transcript);
  } catch (err) {
    setError(getErrorMessage(err));
  } finally {
    setIsTranscribing(false);
    if (audioInputRef.current) audioInputRef.current.value = "";
  }
}
